// src/contexts/InsuranceContext.tsx

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { toast } from "sonner";

import { useInsuranceActivity } from "../hooks/useInsuranceActivity";
import { useInsuranceAuthorizations } from "../hooks/useInsuranceAuthorizations";
import { useInsuranceClaims } from "../hooks/useInsuranceClaims";
import { useInsuranceEligibility } from "../hooks/useInsuranceEligibility";
import { useInsuranceEnrollments } from "../hooks/useInsuranceEnrollments";
import { useInsurancePersistence } from "../hooks/useInsurancePersistence";
import { useInsurancePolicies } from "../hooks/useInsurancePolicies";
import { useInsuranceProviders } from "../hooks/useInsuranceProviders";
import { useInsuranceReports } from "../hooks/useInsuranceReports";
import { useInsuranceSession } from "../hooks/useInsuranceSession";

import {
  checkInsuranceEligibility,
} from "../services/insurance-eligibility.service";

import {
  exportInsuranceData,
} from "../utils/insurance-export.utils";

import {
  generateInsuranceStats,
} from "../utils/insurance-stats.utils";

import type {
  InsuranceExportType,
} from "../utils/insurance-export.utils";

import type {
  ActivityLog,
  Authorization,
  Claim,
  EligibilityCheck,
  EligibilityVerification,
  Enrollment,
  InsuranceContextType,
  Policy,
  Provider,
  Report,
} from "./InsuranceContextType";

import type {
  ReactNode,
} from "react";

export type {
  ActivityLog,
  Authorization,
  AuthorizationStatus,
  Claim,
  ClaimStatus,
  Dependent,
  EligibilityCheck,
  EligibilityVerification,
  Enrollment,
  EnrollmentStatus,
  InsuranceContextType,
  Policy,
  PolicyStatus,
  Provider,
  ProviderStatus,
  Report,
  ServiceDetail,
} from "./InsuranceContextType";

interface InsuranceProviderProps {
  children: ReactNode;
}

const InsuranceContext =
  createContext<InsuranceContextType | undefined>(
    undefined
  );

export function InsuranceProvider({
  children,
}: InsuranceProviderProps) {
  /*
   * Shared insurance domain state.
   *
   * Persistence and business operations are handled by the
   * extracted hooks below.
   */
  const [claims, setClaims] = useState<Claim[]>([]);

  const [policies, setPolicies] = useState<
    Policy[]
  >([]);

  const [enrollments, setEnrollments] =
    useState<Enrollment[]>([]);

  const [
    authorizations,
    setAuthorizations,
  ] = useState<Authorization[]>([]);

  const [providers, setProviders] =
    useState<Provider[]>([]);

  const [activityLogs, setActivityLogs] =
    useState<ActivityLog[]>([]);

  const [
    eligibilityVerifications,
    setEligibilityVerifications,
  ] = useState<EligibilityVerification[]>([]);

  const [reports, setReports] =
    useState<Report[]>([]);

  /*
   * Activity logging
   */
  const {
    logActivity,
    getRecentActivity:
      getRecentActivityFromLogs,
  } = useInsuranceActivity({
    setActivityLogs,
  });

  const getRecentActivity = useCallback(
    (limit = 10) =>
      getRecentActivityFromLogs(
        activityLogs,
        limit
      ),
    [
      activityLogs,
      getRecentActivityFromLogs,
    ]
  );

  /*
   * Persistence and initial loading
   */
  const {
    isLoading: loading,
    refreshData,
  } = useInsurancePersistence({
    claims,
    policies,
    enrollments,
    authorizations,
    providers,
    activityLogs,
    eligibilityVerifications,
    reports,

    setClaims,
    setPolicies,
    setEnrollments,
    setAuthorizations,
    setProviders,
    setActivityLogs,
    setEligibilityVerifications,
    setReports,
  });

  /*
   * Claims
   */
  const {
    addClaim,
    updateClaim,
    deleteClaim,
    approveClaim,
    rejectClaim,
  } = useInsuranceClaims({
    setClaims,
    logActivity,
  });

  /*
   * Policies and plans
   */
  const {
    addPolicy,
    updatePolicy,
    deletePolicy,
    renewPolicy,
    getPlanById,
    updatePlan,
  } = useInsurancePolicies({
    policies,
    setPolicies,
    logActivity,
  });

  /*
   * Enrollments
   */
  const {
    addEnrollment,
    updateEnrollment,
    cancelEnrollment,
  } = useInsuranceEnrollments({
    setEnrollments,
    logActivity,
  });

  /*
   * Pre-authorizations
   */
  const {
    addAuthorization,
    updateAuthorization,
    approveAuthorization,
    denyAuthorization,
  } = useInsuranceAuthorizations({
    setAuthorizations,
    logActivity,
  });

  /*
   * Provider network
   */
  const {
    addProvider,
    updateProvider,
    suspendProvider,
    activateProvider,
  } = useInsuranceProviders({
    setProviders,
    logActivity,
  });

  /*
   * Eligibility verifications
   */
  const {
    addEligibilityVerification,
    updateEligibilityVerification,
  } = useInsuranceEligibility({
    setEligibilityVerifications,
    logActivity,
  });

  /*
   * Reports
   */
  const {
    addReport,
  } = useInsuranceReports({
    setReports,
  });

  /*
   * Session reset
   */
  const {
    logout,
  } = useInsuranceSession({
    setClaims,
    setPolicies,
    setEnrollments,
    setAuthorizations,
    setProviders,
    setActivityLogs,
    setEligibilityVerifications,
    setReports,
  });

  /*
   * Shared dashboard statistics
   */
  const stats = useMemo(
    () =>
      generateInsuranceStats({
        claims,
        policies,
        enrollments,
        authorizations,
        providers,
      }),
    [
      claims,
      policies,
      enrollments,
      authorizations,
      providers,
    ]
  );

  /*
   * One-off eligibility check.
   *
   * The service owns the calculation. The context only coordinates
   * user feedback and activity logging.
   */
  const checkEligibility = useCallback(
    async (
      patientId: string,
      policyNumber: string
    ): Promise<EligibilityCheck | null> => {
      const {
        result,
        error,
      } = checkInsuranceEligibility({
        patientId,
        policyNumber,
        policies,
        claims,
      });

      if (error === "policy_not_found") {
        toast.error("Policy not found");
        return null;
      }

      if (!result) {
        return null;
      }

      logActivity({
        type: "eligibility",
        action: "Eligibility Checked",
        description: `Eligibility checked for policy ${policyNumber}`,
        performedBy: "System",
        entityId: result.id,
        entityType: "eligibility",
      });

      return result;
    },
    [
      claims,
      policies,
      logActivity,
    ]
  );

  /*
   * Shared JSON export.
   *
   * Page-specific CSV exports remain in their page hooks.
   */
  const exportData = useCallback(
    (
      type: InsuranceExportType
    ) => {
      try {
        exportInsuranceData({
          type,
          claims,
          policies,
          enrollments,
          authorizations,
          providers,
        });

        toast.success(
          "Insurance data exported successfully"
        );
      } catch {
        toast.error(
          "Unable to export insurance data"
        );
      }
    },
    [
      claims,
      policies,
      enrollments,
      authorizations,
      providers,
    ]
  );

  /*
   * Memoizing the value prevents context consumers from receiving
   * a completely new object when no dependency has changed.
   */
  const contextValue =
    useMemo<InsuranceContextType>(
      () => ({
        // Data
        claims,
        policies,
        enrollments,
        authorizations,
        providers,
        activityLogs,
        eligibilityVerifications,
        reports,

        // Loading
        loading,

        // Statistics
        stats,

        // Claims
        addClaim,
        updateClaim,
        deleteClaim,
        approveClaim,
        rejectClaim,

        // Policies
        addPolicy,
        updatePolicy,
        deletePolicy,
        renewPolicy,

        // Enrollments
        addEnrollment,
        updateEnrollment,
        cancelEnrollment,

        // Authorizations
        addAuthorization,
        updateAuthorization,
        approveAuthorization,
        denyAuthorization,

        // Providers
        addProvider,
        updateProvider,
        suspendProvider,
        activateProvider,

        // Activity
        logActivity,
        getRecentActivity,

        // Shared utilities
        checkEligibility,
        refreshData,
        exportData,

        // Plans
        plans: policies,
        getPlanById,
        updatePlan,

        // Eligibility verification
        addEligibilityVerification,
        updateEligibilityVerification,

        // Reports
        addReport,

        // Session
        logout,
      }),
      [
        claims,
        policies,
        enrollments,
        authorizations,
        providers,
        activityLogs,
        eligibilityVerifications,
        reports,

        loading,
        stats,

        addClaim,
        updateClaim,
        deleteClaim,
        approveClaim,
        rejectClaim,

        addPolicy,
        updatePolicy,
        deletePolicy,
        renewPolicy,

        addEnrollment,
        updateEnrollment,
        cancelEnrollment,

        addAuthorization,
        updateAuthorization,
        approveAuthorization,
        denyAuthorization,

        addProvider,
        updateProvider,
        suspendProvider,
        activateProvider,

        logActivity,
        getRecentActivity,

        checkEligibility,
        refreshData,
        exportData,

        getPlanById,
        updatePlan,

        addEligibilityVerification,
        updateEligibilityVerification,

        addReport,
        logout,
      ]
    );

  return (
    <InsuranceContext.Provider
      value={contextValue}
    >
      {children}
    </InsuranceContext.Provider>
  );
}

export function useInsurance(): InsuranceContextType {
  const context =
    useContext(InsuranceContext);

  if (context === undefined) {
    throw new Error(
      "useInsurance must be used within an InsuranceProvider"
    );
  }

  return context;
}

export default InsuranceContext;